import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  prefix = '',
  suffix = '',
  label,
  disabled = false,
  className,
}) => {
  const groupDigits = step >= 1000 || max >= 1_000_000;
  const [isFocused, setIsFocused] = useState(false);

  const formatGrouped = (v: number): string => {
    if (!Number.isFinite(v)) return '';
    const s = String(v);
    const [intPart, decPart] = s.split('.');
    const intStr = intPart ?? s;
    const grouped = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
  };

  const stripGroups = (s: string): string => s.replace(/,/g, '');
  const inputId = label
    ? `${label}`
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-_]/g, '')
    : undefined;

  const quantise = (v: number) => {
    const s = step.toString();
    const dot = s.indexOf('.');
    const decimals = dot === -1 ? 0 : s.length - dot - 1;
    const factor = Math.pow(10, decimals);
    return Math.round(v * factor) / factor;
  };

  const displayFor = (v: number) =>
    groupDigits && !isFocused
      ? formatGrouped(quantise(v))
      : String(quantise(v));
  const [draftValue, setDraftValue] = useState<string>(() => displayFor(value));

  useEffect(() => {
    if (isFocused) return;
    setDraftValue(displayFor(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, step, isFocused]);

  const numericPattern = /^-?\d+(\.\d+)?$/;
  const trimmedDraft = stripGroups((draftValue ?? '').trim());
  const computedInvalid =
    trimmedDraft !== '' && !numericPattern.test(trimmedDraft);

  const handleIncrement = () => {
    const newValue = Math.min(max, quantise(value + step));
    // Sync the displayed draft so keyboard/spinner steps update visibly even
    // while the input still has focus (the resync useEffect skips while
    // isFocused to protect typed drafts, so we reflect step actions directly).
    setDraftValue(displayFor(newValue));
    onChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max(min, quantise(value - step));
    setDraftValue(displayFor(newValue));
    onChange(newValue);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>
  ) => {
    const inputValue = (e.currentTarget as HTMLInputElement).value;
    setDraftValue(inputValue);

    if (disabled) return;
    if (inputValue === '') return;

    const trimmed = stripGroups(inputValue.trim());
    if (!numericPattern.test(trimmed)) return;

    let numValue = parseFloat(trimmed);
    if (Number.isNaN(numValue)) return;

    numValue = Math.max(min, Math.min(max, numValue));
    onChange(numValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (disabled) return;

    const trimmed = stripGroups((draftValue ?? '').trim());
    if (!trimmed || !numericPattern.test(trimmed)) {
      setDraftValue(displayFor(min));
      onChange(min);
      return;
    }

    let numValue = parseFloat(trimmed);
    if (Number.isNaN(numValue)) {
      setDraftValue(displayFor(min));
      onChange(min);
      return;
    }

    numValue = Math.max(min, Math.min(max, numValue));
    setDraftValue(
      groupDigits ? formatGrouped(quantise(numValue)) : String(numValue)
    );
    onChange(numValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (groupDigits) setDraftValue(String(quantise(value)));
  };

  // Keyboard accessibility: chevron buttons are out of tab order so the input
  // itself owns ArrowUp / ArrowDown stepping (PageUp/Down for ×10).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleDecrement();
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      const next = Math.min(max, quantise(value + step * 10));
      setDraftValue(displayFor(next));
      onChange(next);
    } else if (e.key === 'PageDown') {
      e.preventDefault();
      const next = Math.max(min, quantise(value - step * 10));
      setDraftValue(displayFor(next));
      onChange(next);
    }
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label
          className="text-xs font-medium text-text-secondary"
          htmlFor={inputId}
        >
          {label}
        </label>
      )}
      <div
        className={cn(
          // Mobile: 44px touch target (Apple HIG / WCAG 2.5.5). Desktop: 32px.
          'relative flex items-center h-11 md:h-8 rounded-md',
          'bg-surface-container border border-border-subtle',
          'focus-within:ring-2 focus-within:ring-accent-primary focus-within:border-transparent',
          computedInvalid && 'ring-1 ring-red-500 border-red-500',
          disabled && 'opacity-50'
        )}
      >
        {prefix && (
          <span className="pl-2.5 text-text-muted text-xs font-mono whitespace-nowrap select-none">
            {prefix}
          </span>
        )}
        <input
          type="text"
          id={inputId}
          value={draftValue}
          onChange={handleInputChange}
          onInput={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          inputMode="decimal"
          aria-label={label ?? 'Value'}
          aria-invalid={computedInvalid ? 'true' : undefined}
          className={cn(
            'flex-1 min-w-0 h-full bg-transparent outline-none border-0',
            'text-text-primary text-sm font-mono',
            'disabled:cursor-not-allowed',
            !prefix && 'pl-2.5',
            // Reserve space on the right for the inline stepper column (40 px)
            // + optional suffix label. Wider gutter on mobile keeps the digit
            // visible and the +/- buttons fully tappable.
            suffix ? 'pr-1' : 'pr-10 md:pr-8',
            'text-left'
          )}
        />
        {suffix && (
          <span className="pl-1 pr-10 md:pr-8 text-text-muted text-xs whitespace-nowrap select-none">
            {suffix}
          </span>
        )}
        {/* Inline stepper - vertically stacked chevrons inside the input.
            Wider on mobile (40 px) so each button hits ≥ 22 px; both still
            sit fully inside the input frame so the "Increase" button can
            never render off-screen on a 393 px viewport. */}
        <div className="absolute right-0 top-0 bottom-0 flex flex-col border-l border-border-subtle w-9 md:w-6">
          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled || value >= max}
            className={cn(
              'flex-1 flex items-center justify-center',
              'text-text-muted hover:text-accent-primary hover:bg-surface-container-high',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted',
              'transition-colors focus:outline-none focus:bg-accent-primary/10',
              'rounded-tr-md'
            )}
            aria-label="Increase value"
          >
            <ChevronUp className="w-4 h-4 md:w-3 md:h-3" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled || value <= min}
            className={cn(
              'flex-1 flex items-center justify-center',
              'border-t border-border-subtle',
              'text-text-muted hover:text-accent-primary hover:bg-surface-container-high',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted',
              'transition-colors focus:outline-none focus:bg-accent-primary/10',
              'rounded-br-md'
            )}
            aria-label="Decrease value"
          >
            <ChevronDown className="w-4 h-4 md:w-3 md:h-3" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NumberInput;
